import { z } from 'zod';
// Spec a41ace97 — "one sentence, with its receipts".
//
// A founder opened a campaign 16h after launching it and saw $0.00, 0
// conversions, 0 impressions, 0 clicks, a LIVE chip, and — as the last line
// of a grey footnote — "lost ~86% of impressions to BUDGET. lever: raise its
// daily budget". The numbers were right and the sentence was wrong: the row
// beside it said days_budget_capped = 0 and cost_usd = 0.0000, and a
// budget-capped campaign spends its budget rather than never entering an
// auction. Their question was "is the campaign even running?".
//
// Two rules come out of that, and this file is where they are enforced
// rather than remembered:
//
//   1. Nothing reaches the founder as a number. It reaches them as a
//      sentence with a verdict and at most one lever.
//   2. Every sentence carries the readings that produced it. A verdict the
//      founder cannot check is exactly what that footnote already was.
//
// The second rule is what makes the first safe, which is why `basis` is
// required here and not a rendering nicety.
/** One reading that contributed to a verdict.
 *
 *  `source` carries WHERE and WHEN, because metrics refresh every ~4h and a
 *  present-tense sentence rendered from a 20h-old row is a lie with a
 *  timestamp. The founder sees this string, so it is prose, not an enum. */
export const campaignReadingSchema = z.object({
    /** What was measured — "impressions, first 16h". */
    label: z.string().min(1).max(80),
    /** The reading itself, already formatted. "0", "$21.10", "11 of 12". */
    value: z.string().min(1).max(80),
    /** Where it came from and when it was read — "google_paid_ad_metrics_history · fetched 06:42Z". */
    source: z.string().min(1).max(160),
});
/** Where a campaign is in its life, as far as the evidence can tell.
 *
 *  `pending` and `not_serving` are the pair this whole design exists for.
 *  Today they render identically — as silent zeros — and they mean opposite
 *  things: `pending` is "ask me later", `not_serving` is "something is wrong
 *  and here is what". */
export const campaignVerdictStateSchema = z.enum([
    /** Launched, too young to judge. Zeros here are expected. */
    'pending',
    /** Old enough that zeros are a finding, not a wait. */
    'not_serving',
    /** Serving, but too little volume to read anything into it. */
    'serving_thin',
    /** Enough impressions to be learning; not yet enough outcome. */
    'learning',
    /** Enough signal that the numbers mean something. */
    'readable',
]);
/** The single thing the founder should do next, when there is one.
 *
 *  At most ONE. A verdict offering three levers has not decided anything and
 *  hands the decision back, which is the bombardment this design refuses. */
export const campaignVerdictLeverSchema = z.object({
    label: z.string().min(1).max(80),
    /** Stable key the UI maps to an action. Not a URL — the surface decides. */
    action: z.string().min(1).max(60),
});
/** One sentence, the readings behind it, and what it is entitled to claim. */
export const campaignVerdictSchema = z.object({
    state: campaignVerdictStateSchema,
    /** Mag voice, one line, the whole answer. Not a headline over a table. */
    sentence: z.string().min(1).max(400),
    /** Every reading that produced the sentence.
     *
     *  `.min(1)` is the load-bearing part of this file. A verdict that cannot
     *  name a single reading is an assertion, and an assertion is what shipped
     *  the wrong BUDGET lever. Refusing it here means the failure is a
     *  construction error at the boundary rather than a confident sentence in
     *  front of a founder. */
    basis: z.array(campaignReadingSchema).min(1).max(8),
    lever: campaignVerdictLeverSchema.optional(),
    /** What this verdict is entitled to claim, in the founder's words.
     *
     *  campaign-health-panel.tsx already learned this the expensive way: it
     *  said "every keyword here is priced to compete. nothing in the setup is
     *  holding it back" directly above a line reading "all 9 keywords — 7
     *  rarely served". Both were true. The first is a claim about bids; the
     *  second is a claim about the campaign, from a check that skipped seven
     *  of nine keywords because Google never quoted a price for them.
     *
     *  Carrying scope on the verdict is how a narrow check is stopped from
     *  silently widening into a broad claim. */
    scope: z.string().min(1).max(200),
});
/** Build a verdict, refusing the two shapes that make one untrustworthy.
 *
 *  Use this rather than the schema directly. `campaignVerdictSchema.parse`
 *  enforces the empty-basis rule but cannot see the second one, which is
 *  about what the readings SAY rather than how many there are. */
export function buildCampaignVerdict(input) {
    const verdict = campaignVerdictSchema.parse(input);
    if (verdict.state === 'not_serving' && !citesACause(verdict)) {
        throw new Error('campaign verdict: a not_serving verdict must cite a reading other than the impression ' +
            'count — "0 impressions because 0 impressions" restates the problem instead of ' +
            'explaining it, and tells the founder nothing they could not already see.');
    }
    return verdict;
}
/** Does this verdict's basis contain anything beyond the impression count?
 *
 *  The impression count is the OBSERVATION that something is wrong; on its
 *  own it is never the EXPLANATION. A not_serving verdict has to bring at
 *  least one other reading — keywords Google will not price, a sibling on
 *  the same account that is serving, budget never capped — or it is just the
 *  zero the founder was already looking at, restated with confidence. */
function citesACause(verdict) {
    return verdict.basis.some((reading) => !IMPRESSION_LABEL.test(reading.label));
}
const IMPRESSION_LABEL = /impression/i;
//# sourceMappingURL=campaign-verdict.js.map